import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { toast } from 'react-toastify';
import classes from './editInventoryModal.module.scss';
import Modal from '../../../_common/Modal/Modal';
import Button from '../../../_common/Form/Buttons/Button';
import InputField from '../../../_common/Form/TextFields/InputField';
import DOBPicker from '../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import api from '../../../../api/api';
import Utils from '../../../../utils/utils';

const formatNumber = (value) => (value ? `${+(value)}` : '');

const getInitialData = (inventory) => ({
  ...inventory,
  change: formatNumber(inventory?.change),
  balance: formatNumber(inventory?.balance),
});

const EditInventoryModal = ({ inventory, onClose, onSave }) => {
  const [data, setData] = useState({});
  const [dateError, setDateError] = useState('');
  const [saveLoading, saveLoadingToggle] = useState(false);

  useEffect(() => {
    setData(getInitialData(inventory));

    setDateError('');
  }, [inventory]);

  const changeData = (path, value) => setData((prev) => ({ ...prev, [path]: value }));

  const saveInventory = async () => {
    if (data.expiration && data.expiration.length !== 10) {
      setDateError('Invalid date');

      return;
    }

    saveLoadingToggle(true);

    try {
      await api.updateInventory(data);

      await onSave();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Something went wrong');
    }

    saveLoadingToggle(false);
  };

  return (
    <Modal
      open={!!inventory}
      onClose={onClose}
      className={classes.edit_modal}
    >
      <div className={classes.edit_modal_wrapper}>
        <h2>Edit inventory</h2>

        <div className={classes.fields}>
          <InputField
            label="Date"
            value={inventory?.date ? moment(inventory.date).format('MM/DD/YYYY') : '-'}
            disabled
          />

          <InputField
            label="Reason"
            value={inventory?.reason || '-'}
            disabled
          />

          <InputField
            label="Patient"
            value={inventory?.patient || '-'}
            disabled
          />

          <InputField
            label="RX"
            value={inventory?.rxNumber || '-'}
            disabled
          />

          <InputField
            label="Balance"
            value={data.balance || '-'}
            disabled
          />

          <InputField
            label="Change"
            value={data.change}
            onChange={(value) => changeData('change', value)}
            onKeyPress={(e) => Utils.keyPressCurrency(e, false, undefined, 2, true)}
            onPaste={(e) => Utils.keyPressCurrency(e, true, undefined, 2, true)}
          />

          <InputField
            label="Lot"
            value={data.lot}
            onChange={(value) => changeData('lot', value)}
          />

          <DOBPicker
            label="Expires"
            date={data.expiration}
            dateFormat="YYYY-MM-DD"
            autoValidation={false}
            error={dateError}
            onChange={(date) => {
              setDateError('');

              changeData('expiration', date);
            }}
          />

          <div className={classes.memo}>
            <InputField
              label="Memo"
              value={data.memo}
              onChange={(value) => changeData('memo', value)}
            />
          </div>
        </div>

        <div className={classes.btns_wrapper}>
          <Button
            title="Cancel"
            type="button"
            onClick={onClose}
            clearButton
          />

          <Button
            title="Save"
            type="button"
            onClick={saveInventory}
            loading={saveLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

EditInventoryModal.propTypes = {
  inventory: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditInventoryModal;
